import React, { useState, useRef, useEffect } from "react";
import { ThumbsUp } from "lucide-react";
import { api } from "@/lib/api";
import { requireLogin } from "@/lib/guestGate";
import { useApp } from "@/context/AppContext";
import { ReactorsDialog } from "@/components/ReactorsDialog";
import { compactNumber } from "@/lib/formatNumber";
import { toast } from "sonner";

const EMOJI = { love: "👍", adore: "❤️", amen: "🙏", gloire: "🙌", sad: "😢", rire: "😂", wow: "😮" };

// Compact per-media reaction bar (album photo/video) — Facebook-style, used only in
// the fullscreen theater/viewer. Mirrors ReactionBar's picker UX but targets the
// per-media endpoints. `dark` styles it for a black background.
export const MediaReactionBar = ({ postId, mediaIndex, initial = {}, dark = false, onChange, testId = "media-reactions" }) => {
  const { config, user, t, lang } = useApp();
  const reactions = config?.reactions || [];
  const [myReaction, setMyReaction] = useState(initial.my || null);
  const [counts, setCounts] = useState(initial.counts || {});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reactorsOpen, setReactorsOpen] = useState(false);
  const closeTimer = useRef(null);
  const longPressTimer = useRef(null);
  const suppressClick = useRef(false);
  const barRef = useRef(null);
  const dirtyRef = useRef(false); // true once the member reacts → never clobber their choice
  const canHover = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: hover)").matches;

  // Self-hydrate from `initial` when the async summary arrives (the parent may mount
  // us before GET /media-reactions resolves). Skip once the member has reacted.
  const initKey = JSON.stringify({ c: initial.counts || {}, m: initial.my || null });
  useEffect(() => {
    if (dirtyRef.current) return;
    setCounts(initial.counts || {});
    setMyReaction(initial.my || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initKey]);

  useEffect(() => {
    if (!pickerOpen || canHover) return undefined;
    const onDown = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [pickerOpen, canHover]);

  const openPicker = () => { clearTimeout(closeTimer.current); setPickerOpen(true); };
  const scheduleClose = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setPickerOpen(false), 350); };

  const emit = (nc, nm) => onChange?.(nc, nm);

  const react = async (key) => {
    setPickerOpen(false);
    if (!user) { requireLogin("react"); return; }
    dirtyRef.current = true;
    const wasSame = myReaction === key;
    try {
      if (wasSame) {
        await api.delete(`/posts/${postId}/media/${mediaIndex}/react`);
        const nc = { ...counts, [key]: Math.max(0, (counts[key] || 1) - 1) };
        setMyReaction(null); setCounts(nc); emit(nc, null);
      } else {
        await api.post(`/posts/${postId}/media/${mediaIndex}/react`, { type: key });
        const nc = { ...counts, [key]: (counts[key] || 0) + 1 };
        if (myReaction) nc[myReaction] = Math.max(0, (nc[myReaction] || 1) - 1);
        setMyReaction(key); setCounts(nc); emit(nc, key);
      }
    } catch (e) {
      toast.error(e?.friendlyMessage || "Erreur");
    }
  };

  const mainClick = () => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    if (!canHover) { setPickerOpen((v) => !v); return; }
    react(myReaction || reactions[0]?.key || "love");
  };
  const touchStart = () => { longPressTimer.current = setTimeout(() => { suppressClick.current = true; openPicker(); }, 450); };
  const touchEnd = () => clearTimeout(longPressTimer.current);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const rl = (r) => (r ? r[`label_${lang}`] || r.label_fr || r.key : "");
  const current = reactions.find((r) => r.key === myReaction);
  const mainLabel = current ? rl(current) : (rl(reactions[0]) || t("post.react", "Réagir"));
  const activeReactions = reactions.filter((r) => (counts[r.key] || 0) > 0).slice(0, 3);
  const txt = dark ? "rgba(255,255,255,0.85)" : "var(--yalola-text-muted)";
  const mainColor = current ? current.color : txt;

  return (
    <div ref={barRef} className="relative flex items-center justify-between gap-2" data-testid={testId}>
      {pickerOpen && (
        <div
          className="absolute -top-[58px] left-0 flex items-end gap-1 bg-white rounded-full shadow-xl border px-2.5 py-2 z-[80] overflow-x-auto hide-scrollbar max-w-[calc(100vw-2rem)]"
          style={{ borderColor: "var(--yalola-border)" }}
          onMouseEnter={canHover ? openPicker : undefined}
          onMouseLeave={canHover ? scheduleClose : undefined}
          data-testid={`${testId}-picker`}
        >
          {reactions.map((r) => (
            <button
              key={r.key}
              onClick={() => react(r.key)}
              title={rl(r)}
              className="flex flex-col items-center group w-9 sm:w-10 shrink-0"
              data-testid={`${testId}-react-${r.key}`}
            >
              <span
                className="text-[28px] sm:text-[30px] leading-none transition-transform duration-150 group-hover:scale-[1.25] group-hover:-translate-y-1.5 rounded-full"
                style={{ background: myReaction === r.key ? "var(--yalola-primary-soft)" : "transparent", padding: 2 }}
              >
                {EMOJI[r.key] || "👍"}
              </span>
            </button>
          ))}
        </div>
      )}

      <button
        onClick={mainClick}
        onMouseEnter={canHover ? openPicker : undefined}
        onMouseLeave={canHover ? scheduleClose : undefined}
        onTouchStart={touchStart}
        onTouchEnd={touchEnd}
        className="flex items-center gap-1.5 py-1.5 px-2.5 rounded-full text-[13px] font-semibold transition-colors hover:bg-black/10"
        style={{ color: mainColor }}
        data-testid={`${testId}-main`}
      >
        {current ? <span className="text-[18px] leading-none">{EMOJI[current.key] || "👍"}</span> : <ThumbsUp size={17} />}
        <span>{mainLabel}</span>
      </button>

      {total > 0 && (
        <button
          onClick={() => setReactorsOpen(true)}
          className="flex items-center gap-1.5 py-1.5 px-2 rounded-full hover:bg-black/10 transition-colors"
          style={{ color: txt }}
          data-testid={`${testId}-total`}
        >
          <div className="flex -space-x-1">
            {activeReactions.map((r) => (
              <span key={r.key} className="h-[22px] w-[22px] rounded-full grid place-items-center text-[14px] leading-none bg-white border shadow-sm" style={{ borderColor: "var(--yalola-border)" }}>
                {EMOJI[r.key] || "👍"}
              </span>
            ))}
          </div>
          <span className="text-xs font-semibold">{compactNumber(total)}</span>
        </button>
      )}

      <ReactorsDialog
        open={reactorsOpen}
        onOpenChange={setReactorsOpen}
        endpoint={`/posts/${postId}/media/${mediaIndex}/reactions`}
        testId={`${testId}-reactors`}
      />
    </div>
  );
};

export default MediaReactionBar;
